import React, { useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import {
  ArrowRight,
  Copy,
  Eye,
  FileText,
  Loader2,
  MoreHorizontal,
  Trash2,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { EmptyState } from "~/components/ui/empty-state";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import TabSectionHeader from "~/pages/clients/components/client-detail/TabSectionHeader";
import QuotationStatusBadge from "~/pages/presupuestos/components/QuotationStatusBadge";
import { useClientQuotations } from "~/pages/presupuestos/hooks/useQuotations";
import { canConvertQuotation, canDeleteQuotation } from "~/types/quotation";
import { formatCurrency } from "~/utils/helpers";
import { generateQuotationPdf } from "~/utils/quotationPdf";

interface ClientCotizacionesTabProps {
  clientId: string;
}

const ClientCotizacionesTab: React.FC<ClientCotizacionesTabProps> = ({
  clientId,
}) => {
  const navigate = useNavigate();
  const [generatingPdfId, setGeneratingPdfId] = useState<string | null>(null);
  const {
    quotations,
    isLoading,
    handleDelete,
    handleConvertToSalesRequest,
    handleDuplicate,
  } = useClientQuotations(clientId);

  const handleConvert = async (quotationId: string) => {
    const salesRequestId = await handleConvertToSalesRequest(quotationId);
    if (salesRequestId) {
      navigate(`/notas-de-venta/${salesRequestId}`);
    }
  };

  const handleGeneratePdf = async (quotation: (typeof quotations)[0]) => {
    setGeneratingPdfId(quotation.id);
    try {
      await generateQuotationPdf(quotation);
    } finally {
      setGeneratingPdfId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-4 pt-6 md:p-6">
        <TabSectionHeader title="Cotizaciones" />
        <TableSkeleton rows={6} columns={6} />
      </div>
    );
  }

  if (quotations.length === 0) {
    return (
      <div className="space-y-6 p-4 pt-6 md:p-6">
        <TabSectionHeader title="Cotizaciones" />
        <EmptyState
          icon={FileText}
          title="No hay cotizaciones para este cliente"
          description='Usa el botón "Nueva Cotización" para crear una nueva cotización para este cliente.'
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 pt-6 md:p-6">
      <TabSectionHeader
        title="Cotizaciones"
        description={`${quotations.length} ${quotations.length === 1 ? "cotización" : "cotizaciones"}`}
      />
      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead>N°</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Válido hasta</TableHead>
              <TableHead>Creado</TableHead>
              <TableHead className="w-[100px]">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {quotations.map((quotation, index) => (
              <TableRow
                key={quotation.id}
                className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
                style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                onClick={() => navigate(`/quotations/${quotation.id}`)}
              >
                <TableCell className="font-medium">
                  #{String(quotation.quotation_number).padStart(4, "0")}
                </TableCell>
                <TableCell>
                  {formatCurrency(quotation.price_with_iva || 0)}
                </TableCell>
                <TableCell>
                  <QuotationStatusBadge status={quotation.status} />
                </TableCell>
                <TableCell>
                  <span
                    className={
                      dayjs(quotation.valid_until).isBefore(dayjs())
                        ? "text-red-600"
                        : ""
                    }
                  >
                    {dayjs(quotation.valid_until).format("DD/MM/YYYY")}
                  </span>
                </TableCell>
                <TableCell>
                  {dayjs(quotation.created_at).format("DD/MM/YYYY")}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => navigate(`/quotations/${quotation.id}`)}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Ver detalle
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onSelect={(e) => {
                          e.preventDefault();
                          void handleGeneratePdf(quotation);
                        }}
                        disabled={generatingPdfId === quotation.id}
                      >
                        {generatingPdfId === quotation.id ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <FileText className="mr-2 h-4 w-4" />
                        )}
                        {generatingPdfId === quotation.id
                          ? "Generando PDF..."
                          : "Generar PDF"}
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={() => handleDuplicate(quotation.id)}
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Duplicar
                      </DropdownMenuItem>

                      {canConvertQuotation(quotation.status) && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleConvert(quotation.id)}
                          >
                            <ArrowRight className="mr-2 h-4 w-4" />
                            Convertir a Nota de Venta
                          </DropdownMenuItem>
                        </>
                      )}

                      {canDeleteQuotation(quotation.status) && (
                        <>
                          <DropdownMenuSeparator />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem
                                onSelect={(e) => e.preventDefault()}
                                className="text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Eliminar
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  ¿Eliminar presupuesto?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Esta acción no se puede deshacer.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(quotation.id)}
                                  className="bg-destructive text-destructive-foreground"
                                >
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ClientCotizacionesTab;
